import React, { useMemo, useState } from 'react';
import { useIntl } from 'umi';
import { isEmpty } from 'lodash-es';
import Header from '@/pages/datalab/components/Header';
import ConfirmAndCancel from '@/pages/datalab/components/ConfirmAndCancle';
import { createEmptyUserControl } from '@/pages/datalab/components/DashboardLayout/_utils';
import {
  allowChartTypeShowUserFilter,
  allowShowUserFilter,
} from '@dbl/core/global';
import CollapseFilterSection from './components/CollapseFilterSection';
import GeneralFilterSection from './components/GeneralFilterSection';
import UserFilterSection from './components/UserFilterSection';
import type { IFilterSetUpConfig, IFilterSetUpProps } from './types';
import {
  buildScopeChartsFromSelectedIds,
  getScopeChartIds,
  hasUserFilterControls,
} from './utils';

const FilterSetUp: React.FC<IFilterSetUpProps> = (props) => {
  const {
    onConfirm,
    onClose,
    chartList: originChartList,
    generalFilters,
    userFilter,
    gameCode,
    enableCollapseFilter = false,
  } = props;

  const { formatMessage } = useIntl();

  const allowSetUp = useMemo(() => allowShowUserFilter(gameCode), [gameCode]);

  // 过滤未支持 userFilter 的图表类型
  const chartList = useMemo(() => {
    return (
      originChartList?.filter((item) =>
        allowChartTypeShowUserFilter({
          chartType: item.chart_type,
          mode: item.mode,
        }),
      ) || []
    );
  }, [originChartList]);

  const [filterSetUpConfig, setFilterSetUpConfig] = useState<IFilterSetUpConfig>(
    () => {
      const showGeneralFilter = (generalFilters || []).length > 0;
      const showUserFilter = hasUserFilterControls(userFilter);

      return {
        showGeneralFilter,
        showUserFilter,
        showScope: userFilter?.show_scope ?? true,
        controls: userFilter?.controls,
        enableCollapseFilter,
        scopeCharts: (userFilter?.scope_charts ?? chartList ?? []).map((item) => ({
          task_id: item.task_id,
          name: item.name,
          nameCn: item.name_cn,
          chart_type: item.chart_type,
          table_id: item.table_id,
        })),
      };
    },
  );

  return (
    <div className="dbl-filter-setUp">
      <Header title={formatMessage({ id: 'dbl_filter_filter_set_up' })} />
      <div className="dbl-filter-setUp__body">
        {allowSetUp ? (
          <>
            <GeneralFilterSection
              open={filterSetUpConfig.showGeneralFilter}
              onOpenChange={(open: boolean) => {
                setFilterSetUpConfig((prev) => ({
                  ...prev,
                  showGeneralFilter: open,
                }));
              }}
            />

            <UserFilterSection
              open={filterSetUpConfig.showUserFilter}
              onOpenChange={(open: boolean) => {
                setFilterSetUpConfig((prev) => {
                  if (!open) {
                    return {
                      ...prev,
                      showUserFilter: false,
                      showScope: false,
                      scopeCharts: [],
                      controls: [],
                    };
                  }

                  const nextControls =
                    !isEmpty(prev.controls) ? prev.controls : userFilter?.controls;

                  return {
                    ...prev,
                    showUserFilter: true,
                    showScope: true,
                    scopeCharts: chartList,
                    controls: isEmpty(nextControls)
                      ? [createEmptyUserControl()]
                      : nextControls,
                  };
                });
              }}
              showScope={filterSetUpConfig.showScope}
              onShowScopeChange={(show: boolean) => {
                setFilterSetUpConfig((prev) => ({
                  ...prev,
                  showScope: show,
                }));
              }}
              userFilter={userFilter}
              chartList={chartList}
              selectedChartIds={getScopeChartIds(filterSetUpConfig.scopeCharts)}
              onSelectedChartIdsChange={(ids: string[]) => {
                setFilterSetUpConfig((prev) => ({
                  ...prev,
                  scopeCharts: buildScopeChartsFromSelectedIds(chartList, ids),
                }));
              }}
            />
          </>
        ) : null}

        <CollapseFilterSection
          open={Boolean(filterSetUpConfig.enableCollapseFilter)}
          onOpenChange={(open: boolean) => {
            setFilterSetUpConfig((prev) => ({
              ...prev,
              enableCollapseFilter: open,
            }));
          }}
        />
      </div>
      <ConfirmAndCancel
        onConfirm={() => {
          onConfirm?.(filterSetUpConfig);
        }}
        onCancel={onClose}
        cancelLabel={formatMessage({ id: 'dbl_common_back' })}
      />
    </div>
  );
};

export default FilterSetUp;